import {useEffect, useRef} from 'react';

const FOCUSABLE_SELECTOR = [
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const FIELD_SELECTOR = 'input, select, textarea';

// Containers with an active trap, the top-most last: when modals stack
// (one opened from inside another), only the top one keeps focus.
const activeTraps: HTMLElement[] = [];

const isTopTrap = (container: HTMLElement) =>
  activeTraps[activeTraps.length - 1] === container;

// Layout is not consulted (jsdom has none); elements that cannot take focus
// in the browser, such as display: none ones, are skipped when focusing.
const focusablesIn = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) =>
      element.getAttribute('tabindex') !== '-1' &&
      !element.closest('[hidden], [aria-hidden="true"]')
  );

/** Focuses the first candidate that takes focus; true when one did. */
const focusFirstOf = (candidates: HTMLElement[]) =>
  candidates.some((element) => {
    element.focus();

    return document.activeElement === element;
  });

/**
 * Keeps keyboard focus inside `container` while `isActive`. On activation,
 * unless focus is already inside, it moves to the first field (else the
 * first focusable, else the container itself); Tab and Shift+Tab wrap at
 * the ends; once released, focus goes back to the element that had it.
 */
function useFocusTrap(container: HTMLElement | null, isActive: boolean) {
  const returnFocusTo = useRef<Element | null>(null);

  // Declared first, so the opener is recorded before the trap below moves
  // focus into the container.
  useEffect(() => {
    if (!isActive) return;

    returnFocusTo.current = document.activeElement;

    return () => {
      const element = returnFocusTo.current;

      if (element instanceof HTMLElement && element.isConnected) {
        element.focus();
      }
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !container) return;

    activeTraps.push(container);

    if (!container.contains(document.activeElement)) {
      const focusables = focusablesIn(container);
      const fields = focusables.filter((element) =>
        element.matches(FIELD_SELECTOR)
      );

      if (!focusFirstOf(fields) && !focusFirstOf(focusables)) {
        if (!container.hasAttribute('tabindex')) container.tabIndex = -1;
        container.focus();
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !isTopTrap(container)) return;

      const current = focusablesIn(container);

      if (!current.length) {
        event.preventDefault();

        return;
      }

      const active = document.activeElement;
      const edge = event.shiftKey ? current[0] : current[current.length - 1];

      if (active !== edge && container.contains(active)) return;

      // Wrap round to the other end.
      event.preventDefault();
      focusFirstOf(event.shiftKey ? current.reverse() : current);
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      activeTraps.splice(activeTraps.lastIndexOf(container), 1);
    };
  }, [container, isActive]);
}

export default useFocusTrap;
